// C:\Apps\Bresc_Test\bresco-franquicies-frontend\src\components\Siniestros\SiniestrosCompGrid.js
import ColumnFilter from "components/ui/Table/Filters/ColumnFilter";
import DateRangeColumnFilter from "components/ui/Table/Filters/DateRangeColumnFilter";
import SelectColumnFilter from "components/ui/Table/Filters/SelectColumnFilter";
import Table from "components/ui/Table/Table";
import { useModal } from "contexts/ModalContext";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { ImSpinner9 } from "react-icons/im";
import { LuEye, LuSquarePen, LuTrash2, LuDownload, LuSquareCheck, LuSquare, LuMailCheck, LuMail } from "react-icons/lu";
import { Link } from "wouter";
import JsPDF from "jspdf";
import html2canvas from "html2canvas";
import FiniquitoChubbTemplate from "./FiniquitoChubbTemplate";
import BulkEditEstadoModal from "components/ui/Modals/BulkEditEstadoModal";
import BulkSendFiniquitoModal from "components/ui/Modals/BulkSendFiniquitoModal";
import SelectColumnFilterWithCallback from "components/ui/Table/Filters/SelectColumnFilterWithCallback";
import { validarCamposFiniquito } from "./finiquitoValidation";
import BulkSendDocRequestModal from "components/ui/Modals/BulkSendDocRequestModal";
/* eslint-disable react/prop-types */

const formatDateOnly = (value) => {
  if (!value) return "";
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
};

const formatEUR = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(String(value).replace(",", "."));
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);
};

const getAuthToken = (user) => {
  if (!user) return null;
  return user.token || user.accessToken || user.access_token || user.jwt || user.jwt_token || null;
};

async function htmlToPdf({ element, filename }) {
  const canvas = await html2canvas(element, { scale: 1.5, useCORS: true, backgroundColor: "#ffffff" });
  const imgData = canvas.toDataURL("image/jpeg");
  const pdf = new JsPDF("p", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const imgProps = pdf.getImageProperties(imgData);
  const imgHeight = (imgProps.height * pageWidth) / imgProps.width;
  pdf.addImage(imgData, "PNG", 0, 0, pageWidth, imgHeight);
  pdf.save(filename);
}

// ── Filtro multi-select ───────────────────────────────────
// filterValue es un array de strings; soporta también la clave especial para "vacío"
const makeMultiSelectFilter = (emptyKey) => {
  const fn = (rows, id, filterValue) => {
    if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) return rows;
    const values = Array.isArray(filterValue) ? filterValue : [filterValue];
    return rows.filter((row) => {
      const cellVal = String(row.values[id] || "").trim();
      return values.some((v) => {
        if (emptyKey && v === emptyKey) return !row.values[id] || cellVal === "";
        return cellVal === v;
      });
    });
  };
  fn.autoRemove = (val) => !val || (Array.isArray(val) && val.length === 0);
  return fn;
};

const ARCHIVED_ESTADOS = ['LIQUIDADO', 'CANCELADO', 'CERRADO'];

const filterByEstado   = makeMultiSelectFilter("__sin_estado__");
const filterByCausa    = makeMultiSelectFilter("__sin_causa__");
const filterByTipoPago = makeMultiSelectFilter("__sin_tipo_pago__");
const filterByUser     = makeMultiSelectFilter("__sin_modificar__");
const filterByFiniquito = (rows, id, filterValue) => {
  if (!filterValue || filterValue === 'TODOS') return rows;
  if (filterValue === 'ENVIADO') return rows.filter((row) => !!row.values[id]);
  if (filterValue === 'NO_ENVIADO') return rows.filter((row) => !row.values[id]);
  return rows;
};
filterByFiniquito.autoRemove = (val) => !val || val === 'TODOS';

// ── Celda con truncado y tooltip ──────────────────────────
function TruncatedCell({ value, maxChars = 20 }) {
  if (!value) return <span></span>;
  const str = String(value);
  if (str.length <= maxChars) return <span>{str}</span>;
  return (
    <span title={str} className="cursor-help">
      {str.substring(0, maxChars)}…
    </span>
  );
}
TruncatedCell.propTypes = { value: PropTypes.string, maxChars: PropTypes.number };

export default function SiniestrosCompGrid({ user, data, handleRefresh, handleDelete, handleAnalyzeExcel, onRequestArchived, archivedLoaded }) {
  const { openModal } = useModal();
  const [tableData, setTableData] = useState(null);
  const printRef = useRef(null);
  const [printData, setPrintData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [elegiblesParaEnviar, setElegiblesParaEnviar] = useState([]);
  const [showDocRequestModal, setShowDocRequestModal] = useState(false);
  const [elegiblesDocRequest, setElegiblesDocRequest] = useState([]);
  const pageRowIdsRef = useRef([]);

  const handlePageRowsChange = useCallback((pageRows) => {
    pageRowIdsRef.current = pageRows.map((row) => row?.original?.Siniestros_Id).filter(Boolean);
  }, []);

  const apiBaseUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || "";
  const token = getAuthToken(user);
  // const didInitialRefresh = useRef(false);

  // useEffect(() => {
  //   if (!didInitialRefresh.current && token) {
  //     didInitialRefresh.current = true;
  //     handleRefresh();
  //   }
  // }, [token, handleRefresh]);

  const customISOSort = (rowA, rowB, columnId) => {
    const a = rowA?.values?.[columnId];
    const b = rowB?.values?.[columnId];
    const da = a ? new Date(a) : null;
    const db = b ? new Date(b) : null;
    if (!da || isNaN(da.getTime())) return 1;
    if (!db || isNaN(db.getTime())) return -1;
    return da.getTime() - db.getTime();
  };

  const someSelected = selectedIds.size > 0;

  const allPageSelected = useMemo(() => {
    const ids = pageRowIdsRef.current;
    return ids.length > 0 && ids.every((id) => selectedIds.has(id));
  }, [selectedIds]);

  const toggleSelectAll = useCallback(() => {
    const ids = pageRowIdsRef.current;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = ids.length > 0 && ids.every((id) => next.has(id));
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const toggleSelectOne = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleDownloadFiniquito = async (siniestroId, rowFallback) => {
    try {
      setIsGenerating(true);
      if (!apiBaseUrl) throw new Error("Falta REACT_APP_API_URL.");
      if (!token) throw new Error("No se encontró token JWT.");
      const sinRes = await fetch(`${apiBaseUrl}/siniestros/${siniestroId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!sinRes.ok) {
        const t = await sinRes.text().catch(() => "");
        throw new Error(`Error obteniendo siniestro (${sinRes.status}). ${t}`);
      }
      const siniestro = await sinRes.json();
      let franquicia = null;
      const codigoFranq = siniestro?.delegacion_propietaria;
      if (codigoFranq) {
        const frRes = await fetch(`${apiBaseUrl}/franquicias/${codigoFranq}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (frRes.ok) franquicia = await frRes.json();
      }
      // ── Validar campos obligatorios antes de generar el PDF ──────
      const erroresCampos = validarCamposFiniquito(siniestro, franquicia);
      if (erroresCampos.length > 0) {
        openModal("FiniquitoErrorModal", {
          errores: erroresCampos,
          siniestroRef: siniestro?.ref_albaran || rowFallback?.Siniestros_Ref_Albaran || String(siniestroId),
        });
        return;
      }

      const dataForTemplate = {
        tipoPago: siniestro?.tipo_pago ?? "",
        fechaHoy: formatDateOnly(siniestro?.updated_at),
        refFast: siniestro?.ref_fast ?? rowFallback?.Siniestros_Ref_Fast ?? "",
        refAlbaran: siniestro?.ref_albaran ?? rowFallback?.Siniestros_Ref_Albaran ?? "",
        aseguradoNombre: siniestro?.nombre_franquiciado ?? rowFallback?.Siniestros_Franquiciado ?? "",
        propiedadFranquicia:
          franquicia?.propiedad_franquicia ??
          siniestro?.nombre_franquiciado ??
          rowFallback?.Siniestros_Franquiciado ?? "",
        fechaSiniestro: formatDateOnly(siniestro?.fecha_siniestro),
        importeNetoCobrar: formatEUR(siniestro?.importe_neto_cobrar),
        cuenta: siniestro?.cuenta ?? ""
      };
      setPrintData(dataForTemplate);
      await new Promise((resolve) => setTimeout(resolve, 80));
      if (!printRef.current) throw new Error("No se pudo renderizar la plantilla.");
      const filename = `Finiquito_${dataForTemplate.refAlbaran || siniestroId}.pdf`;
      await htmlToPdf({ element: printRef.current, filename });
    } catch (err) {
      console.error(err);
      alert(err?.message || "Error generando el PDF");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleBulkConfirm = useCallback(
    async (ids, estadoSiniestro) => {
      if (!token) throw new Error("No se encontró token JWT.");
      if (!apiBaseUrl) throw new Error("Falta REACT_APP_API_URL.");
      const userId = Number(user?.id);
      if (!userId || Number.isNaN(userId)) throw new Error("userId inválido.");
      const res = await fetch(`${apiBaseUrl}/siniestros/${userId}/bulk/estado`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: Array.from(ids), estado_siniestro: estadoSiniestro }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(payload?.error || "Error en la actualización masiva.");
      setSelectedIds(new Set());
      handleRefresh();
    },
    [token, apiBaseUrl, user?.id, handleRefresh]
  );

  const handleSendFiniquitos = useCallback(() => {
    const ids = Array.from(selectedIds);
    const elegibles = (data || []).filter(
      (s) =>
        ids.includes(s.id) &&
        String(s.estado_siniestro || "").trim().toUpperCase() === "ACEPTADO CHUBB"
    );
    setElegiblesParaEnviar(elegibles);
    setShowSendModal(true);
  }, [selectedIds, data])

  const handleSendDocRequest = useCallback(() => {
    const ids = Array.from(selectedIds);
    const elegibles = (data || []).filter(
      (s) =>
        ids.includes(s.id) &&
        String(s.estado_siniestro || "").trim().toUpperCase() === "PENDIENTE DOC MRW"
    );
    setElegiblesDocRequest(elegibles);
    setShowDocRequestModal(true);
  }, [selectedIds, data]);

  const pendingSelectRef = useRef([])

  // Filtro inicial de Estado a partir del query param (?estado_siniestro=...) del Dashboard
  const initialEstadoFilter = useMemo(() => {
    const raw = new URLSearchParams(window.location.search).get("estado_siniestro");
    if (!raw) return [];
    const value = raw === "null" ? "__sin_estado__" : raw;
    return [{ id: "Siniestros_Estado", value: [value] }];
  }, []);

  // Si el estado del query param es archivado, dispara su carga bajo demanda
  useEffect(() => {
    const raw = new URLSearchParams(window.location.search).get("estado_siniestro");
    if (!raw) return;
    if (ARCHIVED_ESTADOS.includes(raw.trim().toUpperCase()) && typeof onRequestArchived === "function") {
      onRequestArchived([raw]);
    }
  }, [onRequestArchived]);

  const columns = useMemo(
    () => [
      // ── Checkbox ─────────────────────── 48px
      {
        id: "selection",
        minWidth: 48,
        Header: () => (
          <button
            type="button"
            title={allPageSelected ? "Deseleccionar visibles" : "Seleccionar visibles"}
            onClick={toggleSelectAll}
            className="flex items-center justify-center text-gray-500 hover:text-[#FCAE52] transition-colors"
          >
            {allPageSelected
              ? <LuSquareCheck size={20} className="text-[#FCAE52]" />
              : <LuSquare size={20} />}
          </button>
        ),
        accessor: "Siniestros_Id_Checkbox",
        disableSortBy: true,
        disableFilters: true,
        showInPDF: false,
        showInExcel: false,
        Cell: (cell) => {
          const id = cell?.cell?.row?.original?.Siniestros_Id;
          const checked = selectedIds.has(id);
          return (
            <div className="flex items-center justify-center">
              <button
                type="button"
                onClick={() => toggleSelectOne(id)}
                className="flex items-center justify-center text-gray-400 hover:text-[#FCAE52] transition-colors"
              >
                {checked
                  ? <LuSquareCheck size={20} className="text-[#FCAE52]" />
                  : <LuSquare size={20} />}
              </button>
            </div>
          );
        },
      },
      // ── Estado ──────────────────────── 180px
      {
        title: "Filtrar por -Estado-",
        Header: "Estado",
        accessor: "Siniestros_Estado",
        minWidth: 210,
        Filter: SelectColumnFilterWithCallback,
        filter: filterByEstado,
        emptyKey: "__sin_estado__",
        extraOptions: ['CERRADO', 'CANCELADO', 'LIQUIDADO'].filter(
          (e) => !Array.isArray(archivedLoaded) || !archivedLoaded.includes(e)
        ),
        pendingSelectRef: pendingSelectRef,   // ← NUEVO
        onSelectOption: (opt) => {            // ← REEMPLAZA el anterior
          const ARCHIVED = ['LIQUIDADO', 'CANCELADO', 'CERRADO']
          const optUpper = String(opt || '').trim().toUpperCase()
          if (ARCHIVED.includes(optUpper)) {
            const yaCargado = Array.isArray(archivedLoaded) && archivedLoaded.includes(optUpper)
            if (!yaCargado) {
              pendingSelectRef.current = [...pendingSelectRef.current, opt]
              onRequestArchived([opt])
            }
          }
        },
        showInPDF: true,
        showInExcel: true,
        Cell: (cell) => {
          const enviado = cell?.cell?.row?.original?.Siniestros_Finiquito_Enviado;
          const docPedida = cell?.cell?.row?.original?.Siniestros_Doc_Pedida;
          return (
            <div className="flex items-center gap-1.5">
              <TruncatedCell value={cell.value} maxChars={24} />
              {enviado && (
                <span title={`Enviado el ${formatDateOnly(enviado)}`} className="text-green-500 flex-shrink-0">
                  <LuMailCheck size={16} />
                </span>
              )}
              {docPedida && (
                <span title={`Doc. pedida el ${formatDateOnly(docPedida)}`} className="text-blue-500 flex-shrink-0">
                  <LuMail size={16} />
                </span>
              )}
            </div>
          );
        }
      },
      // ── Comentarios ─────────────────── 140px
      {
        title: "Filtrar por -Comentarios-",
        Header: "Comentarios",
        accessor: "Siniestros_Comentarios",
        minWidth: 120,
        Filter: ColumnFilter,
        showInPDF: false,
        showInExcel: false,
        Cell: ({ value }) => <TruncatedCell value={value} maxChars={14} />
      },
      // ── ID Interno ──────────────────── 100px
      {
        title: "Filtrar por -ID Interno-",
        Header: "ID Interno",
        accessor: "Siniestros_IDInterno",
        minWidth: 90,
        Filter: ColumnFilter,
        showInPDF: true,
        showInExcel: true,
        Cell: ({ value }) => <TruncatedCell value={value} maxChars={12} />
      },
      // ── Ref. Albarán ────────────────── 130px
      {
        title: "Filtrar por -Ref. Albarán-",
        Header: "Ref. Albarán",
        accessor: "Siniestros_Ref_Albaran",
        minWidth: 130,
        Filter: ColumnFilter,
        showInPDF: true,
        showInExcel: true,
        Cell: ({ value }) => <TruncatedCell value={value} maxChars={14} />
      },
      {
        title: "Filtrar por -Fecha alta-",
        Header: "Fecha alta",
        accessor: "Siniestros_Created_At",
        minWidth: 80,
        Filter: DateRangeColumnFilter,
        filter: "dateBetween",
        sortType: customISOSort,
        Cell: ({ value }) => formatDateOnly(value),
        exportFormat: (value) => formatDateOnly(value),
        showInPDF: true,
        showInExcel: true,
      },
      // ── Fecha día ───────────────────── 100px
      {
        title: "Filtrar por -Fecha día-",
        Header: "Fecha día",
        accessor: "Siniestros_Fecha_Dia",
        minWidth: 80,
        Filter: DateRangeColumnFilter,
        filter: "dateBetween",
        sortType: customISOSort,
        Cell: ({ value }) => formatDateOnly(value),
        exportFormat: (value) => formatDateOnly(value),
        showInPDF: true,
        showInExcel: true
      },
      // ── Fecha siniestro ─────────────── 100px
      {
        title: "Filtrar por -Fecha siniestro-",
        Header: "Fecha siniestro",
        accessor: "Siniestros_Fecha_Siniestro",
        minWidth: 80,
        Filter: DateRangeColumnFilter,
        filter: "dateBetween",
        sortType: customISOSort,
        Cell: ({ value }) => formatDateOnly(value),
        exportFormat: (value) => formatDateOnly(value),
        showInPDF: true,
        showInExcel: true
      },
      // ── Causa ───────────────────────── 100px
      {
        title: "Filtrar por -Causa-",
        Header: "Causa",
        accessor: "Siniestros_Causa",
        minWidth: 90,
        Filter: SelectColumnFilter,
        filter: filterByCausa,
        showInPDF: true,
        showInExcel: true,
        Cell: ({ value }) => <TruncatedCell value={value} maxChars={8} />
      },
      // ── Delegación ──────────────────── 100px
      {
        title: "Filtrar por -Delegación-",
        Header: "Delegación",
        accessor: "Siniestros_Delegacion",
        minWidth: 100,
        Filter: ColumnFilter,
        showInPDF: true,
        showInExcel: true,
        Cell: ({ value }) => <TruncatedCell value={value} maxChars={12} />
      },
      // ── Franquiciado ────────────────── 150px
      {
        title: "Filtrar por -Franquiciado-",
        Header: "Franquiciado",
        accessor: "Siniestros_Franquiciado",
        minWidth: 150,
        Filter: ColumnFilter,
        showInPDF: true,
        showInExcel: true,
        Cell: ({ value }) => <TruncatedCell value={value} maxChars={14} />
      },
      // ── Ref. FAST ───────────────────── 110px
      {
        title: "Filtrar por -Ref. FAST-",
        Header: "Ref. FAST",
        accessor: "Siniestros_Ref_Fast",
        minWidth: 110,
        Filter: ColumnFilter,
        showInPDF: true,
        showInExcel: true,
        Cell: ({ value }) => <TruncatedCell value={value} maxChars={12} />
      },
      // ── Tipo pago ───────────────────── 100px
      {
        title: "Filtrar por -Tipo pago-",
        Header: "Tipo pago",
        accessor: "Siniestros_Tipo_Pago",
        minWidth: 100,
        Filter: SelectColumnFilter,
        filter: filterByTipoPago,
        showInPDF: true,
        showInExcel: true,
        Cell: ({ value }) => <TruncatedCell value={value} maxChars={14} />
      },
      // ── Finiquito enviado ────────────── 100px
      {
        title: "Filtrar por -Finiquito-",
        Header: "Finiquito",
        accessor: "Siniestros_Finiquito_Enviado",
        minWidth: 95,
        Filter: ({ column: { filterValue, setFilter } }) => (
          <select
            value={filterValue || 'TODOS'}
            onChange={(e) => setFilter(e.target.value === 'TODOS' ? undefined : e.target.value)}
            className="w-full text-xs border border-gray-300 rounded px-1 py-0.5 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
          >
            <option value="TODOS">Todos</option>
            <option value="ENVIADO">Enviado</option>
            <option value="NO_ENVIADO">No enviado</option>
          </select>
        ),
        filter: filterByFiniquito,
        emptyKey: "__sin_finiquito__",
        showInPDF: false,
        showInExcel: false,
        Cell: ({ value }) => value
          ? <span className="text-green-600 font-semibold text-xs">Enviado</span>
          : <span className="text-gray-400 text-xs">No enviado</span>,
      },
      // ── Modificado ──────────────────── 120px
      {
        title: "Filtrar por -Modificado-",
        Header: "Modificado",
        accessor: "Siniestros_ModificadoPor",
        minWidth: 100,
        Filter: SelectColumnFilter,
        filter: filterByUser,
        showInPDF: false,
        showInExcel: false,
        Cell: ({ value }) => <TruncatedCell value={value} maxChars={12} />
      },
      // ── Acciones ────────────────────── 140px
      {
        title: "Acciones",
        Header: "",
        accessor: "Siniestros_Actions",
        minWidth: 140,
        disableSortBy: true,
        Cell: (value) => {
          const id = value?.cell?.row?.original?.Siniestros_Id;
          const rowFallback = value?.cell?.row?.original;
          const estadoRow = String(value?.cell?.row?.original?.Siniestros_Estado || '').trim().toUpperCase();
          const esCerrado = ['CERRADO', 'LIQUIDADO', 'ACEPTADO CHUBB', 'ENVIO FINIQUITO MRW', 'RECIBO FINIQUITO FIRMADO'].includes(estadoRow);
          return (
            <div className="flex items-center justify-end gap-x-4">
              {esCerrado && (
                <button
                  title={isGenerating ? "Generando..." : "Descargar finiquito (PDF)"}
                  type="button"
                  disabled={isGenerating}
                  className="text-gray-500 transition-colors duration-200 dark:hover:text-blue-400 dark:text-gray-300 hover:text-blue-500 focus:outline-none disabled:opacity-50"
                  onClick={() => handleDownloadFiniquito(id, rowFallback)}
                >
                  <LuDownload size={18} />
                </button>
              )}
              <Link
                title={user.role_id === 1 ? "Editar registro" : "Ver registro"}
                href={`/siniestros/${id}`}
                className="text-gray-500 transition-colors duration-200 dark:hover:text-yellow-500 dark:text-gray-300 hover:text-yellow-500 focus:outline-none"
              >
                {(user.role_id === 1 || user.role_id === 2 || (user.role_id === 3 && estadoRow === 'REVISADO')) ? <LuSquarePen size={18} /> : <LuEye size={18} />}
              </Link>
              {user.role_id === 1 || user.role_id === 2 ? (
                <button
                  title="Eliminar registro"
                  type="button"
                  className="text-gray-500 transition-colors duration-200 dark:hover:text-red-500 dark:text-gray-300 hover:text-red-500 focus:outline-none"
                  onClick={() =>
                    openModal("DeleteModal", {
                      itemName: value?.cell?.row?.original?.Siniestros_Ref_Albaran || id,
                      itemId: id,
                      handleDelete
                    })
                  }
                >
                  <LuTrash2 size={18} />
                </button>
              ) : null}
            </div>
          );
        },
        showInPDF: false,
        showInExcel: false
      }
    ],
    [openModal, user, handleDelete, isGenerating, selectedIds, toggleSelectOne, toggleSelectAll, allPageSelected, onRequestArchived, archivedLoaded]
  );

  useEffect(() => {
    if (data === undefined || data === null) { setTableData(null); return; }
    if (Array.isArray(data)) {
      setTableData(data.map((item) => ({
        Siniestros_Id: item.id,
        Siniestros_IDInterno: item.IDInterno,
        Siniestros_Estado: item.estado_siniestro,
        Siniestros_Comentarios: item.comentarios || "",
        Siniestros_Ref_Albaran: item.ref_albaran,
        Siniestros_Fecha_Dia: item.fecha_dia,
        Siniestros_Fecha_Siniestro: item.fecha_siniestro,
        Siniestros_Causa: item.causa,
        Siniestros_Delegacion: item.delegacion_propietaria,
        Siniestros_Franquiciado: item.nombre_franquiciado,
        Siniestros_Ref_Fast: item.ref_fast,
        Siniestros_Tipo_Pago: item.tipo_pago,
        Siniestros_ModificadoPor: item.updated_by_full_name || "",
        Siniestros_Created_At: item.created_at,
        Siniestros_Finiquito_Enviado: item.finiquito_enviado_at || null,
        Siniestros_Doc_Pedida: item.doc_pedida_at || null,
      })));
    } else {
      setTableData([]);
    }
  }, [data]);

  useEffect(() => { setSelectedIds(new Set()); }, [data]);

  return (
    <>
      <div style={{ position: "fixed", left: "-99999px", top: 0, width: "794px", background: "#fff", zIndex: -1 }}>
        <div ref={printRef}>{printData ? <FiniquitoChubbTemplate data={printData} /> : null}</div>
      </div>
      {someSelected && (
        <div className="flex items-center gap-3 px-4 py-2.5 mb-3 bg-amber-50 border border-[#FCAE52] rounded-xl">
          <span className="text-sm font-semibold text-gray-700">
            {selectedIds.size} registro{selectedIds.size !== 1 ? "s" : ""} seleccionado{selectedIds.size !== 1 ? "s" : ""}
          </span>
          <button
            type="button"
            onClick={() => setShowBulkModal(true)}
            className="flex items-center gap-1.5 h-8 px-4 text-sm font-semibold text-white bg-[#FCAE52] border border-[#FCAE52] rounded-lg hover:bg-[#e99d3f] transition-colors"
          >
            <LuSquarePen size={15} />
            Editar estado
          </button>
          {(user?.role_id === 1 || user?.role_id === 2) && (
            <button
              type="button"
              onClick={handleSendFiniquitos}
              className="flex items-center gap-1.5 h-8 px-4 text-sm font-semibold text-white bg-green-600 border border-green-600 rounded-lg hover:bg-green-700 transition-colors"
            >
              Enviar finiquito
            </button>
          )}
          {(user?.role_id === 1 || user?.role_id === 2) && (
            <button
              type="button"
              onClick={handleSendDocRequest}
              className="flex items-center gap-1.5 h-8 px-4 text-sm font-semibold text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <LuMail size={15} />
              Pedir documentación
            </button>
          )}
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="h-8 px-3 text-sm text-gray-500 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Limpiar selección
          </button>
        </div>
      )}
      {tableData ? (
        <Table
          tableName={"Siniestros"}
          columns={columns}
          createNewItemURL={"/siniestros/new"}
          data={tableData}
          rawData={data}
          defaultSort={{ id: "Siniestros_Fecha_Siniestro", desc: true }}
          hiddenColumns={[]}
          allowImportExcel={true}
          uploadExcelURL={`siniestros/${user?.id}/import/single`}
          handleRefresh={handleRefresh}
          handleAnalyzeExcel={handleAnalyzeExcel}
          onPageRowsChange={handlePageRowsChange}
          exportMRWUrl={"siniestros/export/mrw"}
          exportExtraParams={{ archivedEstados: archivedLoaded }}
          initialFilters={initialEstadoFilter}
        />
      ) : (
        <div className="flex items-center content-center justify-center w-full h-full">
          <p className="flex flex-col-reverse items-center justify-center h-full font-bold dark:text-[#F5F5F5] text-gray-700" title="Cargando...">
            <ImSpinner9 size={70} className="animate-spin" />
          </p>
        </div>
      )}
      {showSendModal && (
        <BulkSendFiniquitoModal
          onClose={() => { setShowSendModal(false); setElegiblesParaEnviar([]); }}
          elegibles={elegiblesParaEnviar}
          apiBaseUrl={apiBaseUrl}
          token={token}
          userId={Number(user?.id)}
          onSuccess={() => {
            setSelectedIds(new Set());
            handleRefresh();
          }}
        />
      )}
      {showDocRequestModal && (
        <BulkSendDocRequestModal
          onClose={() => { setShowDocRequestModal(false); setElegiblesDocRequest([]); }}
          elegibles={elegiblesDocRequest}
          apiBaseUrl={apiBaseUrl}
          token={token}
          userId={Number(user?.id)}
          onSuccess={() => {
            setSelectedIds(new Set());
            handleRefresh();
          }}
        />
      )}
      {showBulkModal && (
        <BulkEditEstadoModal
          onClose={() => setShowBulkModal(false)}
          selectedIds={Array.from(selectedIds)}
          onConfirm={handleBulkConfirm}
        />
      )}
    </>
  );
}

SiniestrosCompGrid.propTypes = {
  user: PropTypes.object,
  data: PropTypes.array,
  handleRefresh: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleAnalyzeExcel: PropTypes.func.isRequired,
  onRequestArchived: PropTypes.func,
  archivedLoaded: PropTypes.bool,
};